import { useSearchParams } from 'react-router';

interface UseSearchFiltersReturn {
  searchParams: URLSearchParams;
  setFilter: (params: Record<string, string | null | undefined>) => void;
  appendFilter: (key: string, value: string | string[]) => void;
  deleteFilter: (key: string | string[], value?: string) => void;
  clearFilters: () => void;
  replaceFilterKey: (key: string, value: string, keysToRemove: string[]) => void;
}

export function useSearchFilters(): UseSearchFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateFilters = (action: (params: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      action(next);
      return next;
    });
  };

  const setFilter = (params: Record<string, string | null | undefined>) => {
    updateFilters((newParams) => {
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
    });
  };

  const appendFilter = (key: string, values: string | string[]) =>
    updateFilters((params) => {
      if (Array.isArray(values)) {
        params.delete(key);
        values.forEach((v) => params.append(key, v));
        return;
      }

      const currentValues = params.getAll(key);
      if (currentValues.includes(values)) {
        const remaining = currentValues.filter((v) => v !== values);
        params.delete(key);
        remaining.forEach((v) => params.append(key, v));
      } else {
        params.append(key, values);
      }
    });

  const deleteFilter = (key: string | string[], value?: string) =>
    updateFilters((params) => {
      if (Array.isArray(key)) {
        key.forEach((key) => {
          params.delete(key);
        });
      } else {
        params.delete(key, value);
      }
    });

  const replaceFilterKey = (key: string, value: string, keysToRemove: string[] = []) =>
    updateFilters((params) => {
      for (const k of keysToRemove) params.delete(k);
      params.set(key, value);
    });

  const clearFilters = () => setSearchParams();

  return { searchParams, setFilter, appendFilter, deleteFilter, clearFilters, replaceFilterKey };
}
